import TranslationKeys from "../../../../contexts/I18nContext/translationKeys";
import {
  WhmBox,
  WhmFormHelperText,
  WhmStack,
  whmTheme,
  WhmTooltip,
} from "@securitize/reactjs-whm";
import { Translate, TranslateHtml } from "../../../Translation/Translate";
import { InfoCard } from "../../shared/InfoCard";
import React, { useContext, useEffect } from "react";
import { textFieldStyle, wrapBox } from "../../shared/styles";
import { ReactComponent as InfoOutlined } from "@securitize/assets-sec-ui/dist/icons/svg/info.svg";
import { iconSmall } from "../../../shared/iconThemes";
import { useFormikContext } from "formik";
import { I18nContext } from "../../../../contexts/I18nContext";
import { REQUIRED_GENERIC_HIDDEN_MESSAGE } from "../../../../utils/yupUtils";
import { EntityKYBFormType } from "./validations";
import FormikWhmTextField from "../../../shared/FormikWhmTextField";
import { useFlaggedFieldState } from "./entityFieldKeyMap";
import { FlaggedFieldContainer } from "./FlaggedFieldCallout";

interface BusinessInformationEditableCardProps {
  kybPending: boolean;
  isInvestorEu: boolean;
  onNotifyValueChange: (values: EntityKYBFormType) => void;
  flaggedFields: Map<string, string>;
  investorId: string;
  requestedUpdatesRoundId?: string;
}

export const BusinessInformationEditableCard: React.FC<
  React.PropsWithChildren<BusinessInformationEditableCardProps>
> = ({
  kybPending,
  isInvestorEu,
  onNotifyValueChange,
  flaggedFields,
  investorId,
  requestedUpdatesRoundId,
}) => {
  const { getTranslation } = useContext(I18nContext);

  const { handleBlur, values, errors, setFieldValue, setFieldError } =
    useFormikContext<EntityKYBFormType>();

  // // This rule is specific to re-run the validation on leiCode once the isInvestorEu changes
  useEffect(() => {
    setFieldError(
      "businessInformation.leiCode",
      isInvestorEu && !values.businessInformation.leiCode
        ? REQUIRED_GENERIC_HIDDEN_MESSAGE
        : undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInvestorEu]);

  const shouldShowLeiCodeHelperText = () =>
    !!(
      errors.businessInformation?.leiCode &&
      errors.businessInformation.leiCode !== REQUIRED_GENERIC_HIDDEN_MESSAGE
    );

  const websiteFlag = useFlaggedFieldState(
    investorId,
    "businessInformation.website",
    values.businessInformation.website,
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const leiCodeFlag = useFlaggedFieldState(
    investorId,
    "businessInformation.leiCode",
    values.businessInformation.leiCode,
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const sourceOfFundsFlag = useFlaggedFieldState(
    investorId,
    "businessInformation.sourceOfFunds",
    values.businessInformation.sourceOfFunds,
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const lineOfBusinessFlag = useFlaggedFieldState(
    investorId,
    "businessInformation.lineOfBusiness",
    values.businessInformation.lineOfBusiness,
    flaggedFields,
    requestedUpdatesRoundId,
  );
  const hasFlaggedFields =
    websiteFlag.isFlagged ||
    leiCodeFlag.isFlagged ||
    sourceOfFundsFlag.isFlagged ||
    lineOfBusinessFlag.isFlagged;

  return (
    <InfoCard
      title={
        <Translate
          translationKey={
            TranslationKeys.PROFILE_ENTITY_INFORMATION_BUSINESS_INFORMATION_CARD_TITLE
          }
        />
      }
      subtitle={
        <Translate
          translationKey={
            TranslationKeys.PROFILE_ENTITY_INFORMATION_BUSINESS_INFORMATION_CARD_SUBTITLE
          }
        />
      }
      footer={
        !kybPending && (
          <TranslateHtml
            translationKey={
              TranslationKeys.PROFILE_ENTITY_INFORMATION_BUSINESS_INFORMATION_CARD_FOOTER
            }
          />
        )
      }
    >
      <form>
        <WhmStack sx={{ marginTop: "32px" }}>
          {((errors.businessInformation &&
            Object.keys(errors.businessInformation).length > 0) ||
            hasFlaggedFields) && (
            <WhmFormHelperText error sx={{ marginBottom: "16px !important" }}>
              <Translate
                translationKey={
                  TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_GENERIC_SECTION_ERROR
                }
              />
            </WhmFormHelperText>
          )}

          <WhmBox
            sx={{
              ...wrapBox,
              gap: "16px",
            }}
          >
            <FlaggedFieldContainer
              sx={textFieldStyle}
              comment={websiteFlag.isFlagged ? websiteFlag.comment : undefined}
            >
              <FormikWhmTextField
                name="businessInformation.website"
                dataTestId={"profile-entity-information-website-field"}
                placeholder={getTranslation(
                  TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_WEBSITE_PLACEHOLDER,
                )}
                onBlur={(event) => {
                  void setFieldValue(
                    "businessInformation.website",
                    event.target.value.trim(),
                  );
                  onNotifyValueChange(values);
                  handleBlur(event);
                }}
                disabled={!kybPending && !websiteFlag.isFlagged}
                error={
                  !!errors.businessInformation?.website || websiteFlag.isFlagged
                }
                label={
                  <Translate
                    translationKey={
                      TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_WEBSITE
                    }
                  />
                }
              />
            </FlaggedFieldContainer>
            <FlaggedFieldContainer
              sx={textFieldStyle}
              comment={leiCodeFlag.isFlagged ? leiCodeFlag.comment : undefined}
            >
              <WhmStack direction="row" alignItems="center">
                <FormikWhmTextField
                  name="businessInformation.leiCode"
                  helperText={
                    !leiCodeFlag.isFlagged &&
                    shouldShowLeiCodeHelperText() &&
                    errors.businessInformation?.leiCode
                  }
                  onBlur={(event) => {
                    void setFieldValue(
                      "businessInformation.leiCode",
                      event.target.value.trim(),
                    );
                    onNotifyValueChange(values);
                    handleBlur(event);
                  }}
                  disabled={!kybPending && !leiCodeFlag.isFlagged}
                  error={
                    !!errors.businessInformation?.leiCode ||
                    leiCodeFlag.isFlagged
                  }
                  dataTestId={"profile-entity-information-lei-code-field"}
                  label={
                    <Translate
                      translationKey={
                        isInvestorEu
                          ? TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_LEI_CODE_REQUIRED_LABEL
                          : TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_LEI_CODE
                      }
                    />
                  }
                  sx={{ flexGrow: 1, marginRight: "8px" }}
                />
                <WhmTooltip
                  placement="top-start"
                  data-test-id="profile-entity-information-lei-code-tooltip"
                  title={
                    <TranslateHtml
                      translationKey={
                        TranslationKeys.PROFILE_ENTITY_INFORMATION_BUSINESS_INFORMATION_CARD_LEI_TOOLTIP
                      }
                    />
                  }
                >
                  <InfoOutlined
                    style={iconSmall}
                    fill={whmTheme.palette.action.active}
                  />
                </WhmTooltip>
              </WhmStack>
            </FlaggedFieldContainer>
          </WhmBox>
          <WhmBox
            sx={{
              ...wrapBox,
              gap: "16px",
              marginTop: "16px",
            }}
          >
            <FlaggedFieldContainer
              sx={textFieldStyle}
              comment={
                sourceOfFundsFlag.isFlagged
                  ? sourceOfFundsFlag.comment
                  : undefined
              }
            >
              <FormikWhmTextField
                name="businessInformation.sourceOfFunds"
                onBlur={(event) => {
                  void setFieldValue(
                    "businessInformation.sourceOfFunds",
                    event.target.value.trim(),
                  );
                  onNotifyValueChange(values);
                  handleBlur(event);
                }}
                disabled={!kybPending && !sourceOfFundsFlag.isFlagged}
                error={
                  !!errors.businessInformation?.sourceOfFunds ||
                  sourceOfFundsFlag.isFlagged
                }
                placeholder={getTranslation(
                  TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_SOURCE_OF_FUNDS_PLACEHOLDER,
                )}
                multiline
                minRows={3}
                maxRows={4}
                dataTestId={"profile-entity-information-source-of-funds-field"}
                label={
                  <Translate
                    translationKey={
                      TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_SOURCE_OF_FUNDS
                    }
                  />
                }
              />
            </FlaggedFieldContainer>
            <FlaggedFieldContainer
              sx={textFieldStyle}
              comment={
                lineOfBusinessFlag.isFlagged
                  ? lineOfBusinessFlag.comment
                  : undefined
              }
            >
              <FormikWhmTextField
                name="businessInformation.lineOfBusiness"
                onBlur={(event) => {
                  void setFieldValue(
                    "businessInformation.lineOfBusiness",
                    event.target.value.trim(),
                  );
                  onNotifyValueChange(values);
                  handleBlur(event);
                }}
                multiline
                minRows={3}
                maxRows={4}
                placeholder={getTranslation(
                  TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_LINE_OF_BUSINESS_PLACEHOLDER,
                )}
                disabled={!kybPending && !lineOfBusinessFlag.isFlagged}
                error={
                  !!errors.businessInformation?.lineOfBusiness ||
                  lineOfBusinessFlag.isFlagged
                }
                dataTestId={"profile-entity-information-line-of-business-field"}
                label={
                  <Translate
                    translationKey={
                      TranslationKeys.PROFILE_ENTITY_INFORMATION_FIELDS_LINE_OF_BUSINESS
                    }
                  />
                }
              />
            </FlaggedFieldContainer>
          </WhmBox>
        </WhmStack>
      </form>
    </InfoCard>
  );
};
